import { useState, useEffect } from "react";
import { Row, Col, Button, Tab, Tabs, Spinner, Card } from "react-bootstrap";
import { FaCheckDouble, FaSync, FaBell } from "react-icons/fa";
import api from "../../services/api";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Paginacao from "../../common/pagination";
import CardAtendimento from "./Cards/CardAtendimento";
import CardChamado from "./Cards/CardChamado";
import CardFarmaco from "./Cards/CardFarmaco";
import CardFormulario from "./Cards/CardFormulario";
import CardExame from "./Cards/CardExame";
import CardExameBloqueado from "./Cards/CardExameBloqueado";
import CardArquivoAgendamento from "./Cards/CardArquivoAgendamento";
import CardNotificacaoBase from "./Cards/CardNotificacaoBase";
import CardIndicacaoProfissional from "./Cards/CardIndicacaoProfissional";
import CardAgendamentoRealizado from "./Cards/CardAgendamentoRealizado";
import CardAlvara from "./Cards/CardAlvara";

const Notificacoes = () => {
    const [notificacoes, setNotificacoes] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [key, setKey] = useState('todas'); 
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
    const [page, setPage] = useState(1);
    const [tabCounts, setTabCounts] = useState({});
    const [loadingTabs, setLoadingTabs] = useState(true);

    const MySwal = withReactContent(Swal);

    const tabToType = {
        'todas': '',
        'chamados': 'chamado_abertura,chamado_resposta',
        'farmaco': 'farmacovigilancia',
        'atendimentos': 'novo_atendimento',
        'formularios': 'formulario_dinamico',
        'exames': 'nova_solicitacao_exame',
        'exames_bloqueados': 'tentativa_exame_bloqueada',
        'agendamentos_realizados': 'agendamento_realizado',
        'arquivos': 'arquivo_agendamento',
        'alvaras': 'alvara_vencimento',
    };

    useEffect(() => {
        checkTabVisibility();
    }, []);

    useEffect(() => {
        fetchNotificacoes(1, tabToType[key]);
        setPage(1); 
    }, [key]);

    useEffect(() => {
        if (!loadingTabs && key !== "todas" && (tabCounts?.[key] ?? 0) === 0) {
            setKey("todas");
        }
    }, [tabCounts, key, loadingTabs]);

    const checkTabVisibility = async () => {
        setLoadingTabs(true);
        const tabsToCheck = [
            'chamados',
            'farmaco',
            'atendimentos',
            'formularios',
            'exames',
            'exames_bloqueados',
            'agendamentos_realizados',
            'arquivos',
            'alvaras',
        ];
        
        const counts = {};
        
        try {
            await Promise.all(tabsToCheck.map(async (tabKey) => {
                try {
                    const params = { 
                        listar_todas: true, 
                        page_size: 1, 
                        tipo: tabToType[tabKey] 
                    };
                    const response = await api.get("/notificacoes/", { params });
                    const count = response.data.count !== undefined ? response.data.count : (Array.isArray(response.data) ? response.data.length : 0);
                    counts[tabKey] = count;
                } catch (error) {
                    console.error(`Erro ao verificar contagem para tab ${tabKey}:`, error);
                    counts[tabKey] = 0;
                }
            }));
            setTabCounts(counts);
        } catch (error) {
            console.error("Erro geral ao verificar tabs:", error);
        } finally {
            setLoadingTabs(false);
        }
    };

    const fetchNotificacoes = async (pagina = 1, tipo = '') => {
        setLoading(true);
        try {
            const params = { 
                listar_todas: true, 
                page: pagina 
            };
            
            if (tipo) {
                params.tipo = tipo;
            }

            const response = await api.get("/notificacoes/", { params });
            const data = response.data;
            
            const lista = data.results || data; 
            
            if (Array.isArray(lista)) {
                setNotificacoes(lista);
            } else {
                setNotificacoes([]);
            }

            if (data.results) {
                 setPagination({
                    count: data.count,
                    next: data.next,
                    previous: data.previous
                });
            } else {
                setPagination({ count: lista.length, next: null, previous: null });
            }

        } catch (error) {
            console.error("Erro ao buscar notificações:", error);
            setNotificacoes([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchNotificacoes(newPage, tabToType[key]);
    };

    const handleMarcarTodasLidas = async () => {
        try {
            await api.post("/notificacoes/marcar-como-lidas/");
            fetchNotificacoes(1, tabToType[key]); 
            checkTabVisibility(); // Atualiza contagens também, pois pode afetar
            const Toast = MySwal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
            Toast.fire({ icon: 'success', title: 'Todas as notificações marcadas como lidas' });
        } catch (error) {
            console.error("Erro ao marcar como lidas:", error);
        }
    };

    const renderCard = (notif) => {
        if (notif.tipo === 'formulario_dinamico') {
            return <CardFormulario key={notif.id} notif={notif} />;
        }
        if (notif.tipo === 'nova_solicitacao_exame') {
            return <CardExame key={notif.id} notif={notif} />;
        }
        if (notif.tipo === 'tentativa_exame_bloqueada') {
            return <CardExameBloqueado key={notif.id} notif={notif} />;
        }
        if (notif.tipo === 'arquivo_agendamento') {
            return <CardArquivoAgendamento key={notif.id} notif={notif} />;
        }
        if (notif.tipo === 'agendamento_realizado') {
            return <CardAgendamentoRealizado key={notif.id} notif={notif} />;
        }
        if (notif.tipo === 'indicacao_profissional' || notif.tipo === 'indicacao_termo_aceito') {
            return <CardIndicacaoProfissional key={notif.id} notif={notif} />;
        }
        if (notif.tipo === 'alvara_vencimento' && notif.prestador_info) {
            return <CardAlvara key={notif.id} notif={notif} />;
        }
        if (notif.reporte_info) {
             return <CardFarmaco key={notif.id} notif={notif} />;
        }
        if (notif.chamado_info) {
             return <CardChamado key={notif.id} notif={notif} />;
        }
        if (notif.atendimento_info) {
             return <CardAtendimento key={notif.id} notif={notif} />;
        }
        
        // Fallback genérico
        return (
            <CardNotificacaoBase
                key={notif.id}
                notificacao={notif}
                icon={<FaBell />}
                color="secondary"
                text="text-secondary"
                label="Sistema"
                titulo="Notificação do Sistema"
                descricao="Você tem uma nova notificação."
                dataFormatada={
                    (!notif.data_criacao || notif.data_criacao.includes('/')) 
                        ? (notif.data_criacao || "-")
                        : new Date(notif.data_criacao).toLocaleString("pt-BR")
                }
                onClick={() => {}}
            />
        );
    };

    const renderTabContent = (tabKey) => {
        return (
             <>
                {loading ? (
                    <div className="text-center p-5"><Spinner animation="border" /></div>
                ) : (
                    <>
                        {notificacoes.length > 0 ? (
                            notificacoes.map(renderCard)
                        ) : (
                            <div className="alert alert-light text-center">Nenhuma notificação encontrada.</div>
                        )}
                        
                        <div className="mt-3">
                             <Paginacao 
                                dados={pagination}
                                page_numb={page}
                                loading={loading}
                                _onClickPagina={handlePageChange}
                            />
                        </div>
                    </>
                )}
             </>
        );
    };

    return (
            <div>
                <Row className="align-items-center mb-4">
                    <Col>
                        <h4 className="page-title">Central de Notificações</h4>
                    </Col>

                    <Col className="text-end">
                        <Button
                        variant="light"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                            fetchNotificacoes(page, tabToType[key]);
                            checkTabVisibility();
                        }}
                        >
                        <FaSync className={loading || loadingTabs ? "fa-spin" : ""} /> Atualizar
                        </Button>

                        <Button variant="outline-primary" size="sm" onClick={handleMarcarTodasLidas}>
                        <FaCheckDouble /> Marcar todas como lidas
                        </Button>
                    </Col>
                </Row>

                <Tabs
                    id="notificacoes-tabs"
                    activeKey={key}
                    onSelect={(k) => {
                        if (k) setKey(k);
                    }}
                    className="mb-3 aditus-tabs"
                    >
                    <Tab eventKey="todas" title="Todas">
                        {key === "todas" && renderTabContent("todas")}
                    </Tab>

                    {tabCounts?.chamados > 0 && (
                        <Tab eventKey="chamados" title={`Chamados (${tabCounts.chamados})`}>
                        {key === "chamados" && renderTabContent("chamados")}
                        </Tab>
                    )}

                    {tabCounts?.farmaco > 0 && (
                        <Tab eventKey="farmaco" title={`Farmacovigilância (${tabCounts.farmaco})`}>
                        {key === "farmaco" && renderTabContent("farmaco")}
                        </Tab>
                    )}

                    {tabCounts?.atendimentos > 0 && (
                        <Tab eventKey="atendimentos" title={`Atendimentos (${tabCounts.atendimentos})`}>
                        {key === "atendimentos" && renderTabContent("atendimentos")}
                        </Tab>
                    )}

                    {tabCounts?.exames > 0 && (
                        <Tab eventKey="exames" title={`Exames (${tabCounts.exames})`}>
                        {key === "exames" && renderTabContent("exames")}
                        </Tab>
                    )}

                    {tabCounts?.exames_bloqueados > 0 && (
                        <Tab eventKey="exames_bloqueados" title={`Bloqueados (${tabCounts.exames_bloqueados})`}>
                        {key === "exames_bloqueados" && renderTabContent("exames_bloqueados")}
                        </Tab>
                    )}

                    {tabCounts?.agendamentos_realizados > 0 && (
                        <Tab
                        eventKey="agendamentos_realizados"
                        title={`Agendamentos Realizados (${tabCounts.agendamentos_realizados})`}
                        >
                        {key === "agendamentos_realizados" && renderTabContent("agendamentos_realizados")}
                        </Tab>
                    )}

                    {tabCounts?.arquivos > 0 && (
                        <Tab eventKey="arquivos" title={`Arquivos de Agendamentos (${tabCounts.arquivos})`}>
                        {key === "arquivos" && renderTabContent("arquivos")}
                        </Tab>
                    )}

                    {tabCounts?.alvaras > 0 && (
                        <Tab eventKey="alvaras" title={`Alvarás (${tabCounts.alvaras})`}>
                        {key === "alvaras" && renderTabContent("alvaras")}
                        </Tab>
                    )}

                    {tabCounts?.formularios > 0 && (
                        <Tab eventKey="formularios" title={`Formulários (${tabCounts.formularios})`}>
                        {key === "formularios" && renderTabContent("formularios")}
                        </Tab>
                    )}
                </Tabs>
            </div>
        );
};

export default Notificacoes;
